/**
 * Pollinations Audio Service - STT/TTS using OpenAI-Compatible Endpoints
 * Implements Speech-to-Text and Text-to-Speech using Pollinations' OpenAI API
 */

export interface STTRequest {
  audioData: string; // base64 encoded audio
  format: 'wav' | 'mp3' | 'ogg' | 'm4a';
  userId?: string;
}

export interface TTSRequest {
  text: string;
  voice?: 'nova' | 'alloy' | 'echo' | 'fable' | 'onyx' | 'shimmer';
  private?: boolean;
  userId?: string;
}

export interface AudioResponse {
  success: boolean;
  data?: any;
  error?: string;
  audioUrl?: string;
  transcription?: string;
}

import { elevenLabsService } from './elevenlabsService';

class PollinationsAudioService {
  private static instance: PollinationsAudioService;
  private baseUrl = 'https://text.pollinations.ai/openai';
  private apiKey = (import.meta as any).env.VITE_POLLINATION_KEY;
  private referrer = 'lucid-cyber-rpg.app';

  static getInstance(): PollinationsAudioService {
    if (!PollinationsAudioService.instance) {
      PollinationsAudioService.instance = new PollinationsAudioService();
    }
    return PollinationsAudioService.instance;
  }

  /**
   * Convert speech to text using Pollinations STT
   */
  async speechToText(request: STTRequest): Promise<AudioResponse> {
    console.log('[PollinationsAudio] ===== STT REQUEST START =====');
    console.log('[PollinationsAudio] Audio format:', request.format);
    console.log('[PollinationsAudio] Audio data length:', request.audioData.length);

    try {
      // Route through ElevenLabs STT via server proxy
      const el = await elevenLabsService.sttFromBase64(request.audioData, request.format);
      if (el.success) {
        return { success: true, data: el.data, transcription: el.text || '' };
      }
      throw new Error(el.error || 'STT error');
    } catch (error) {
      console.error('[PollinationsAudio] ===== STT FAILED (ElevenLabs) =====');
      console.error('[PollinationsAudio] STT Error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown STT error' };
    }
  }

  /**
   * Convert text to speech using Pollinations TTS
   */
  async textToSpeech(request: TTSRequest): Promise<AudioResponse> {
    console.log('[PollinationsAudio] ===== TTS REQUEST START =====');
    console.log('[PollinationsAudio] Text:', request.text);
    console.log('[PollinationsAudio] Voice:', request.voice || 'nova');

    try {
      // Normalize requested voice to supported Pollinations voices
      const normalizeVoice = (v?: string): string => {
        const voice = (v || 'nova').toLowerCase();
        const supported = new Set(['nova', 'alloy', 'echo', 'fable', 'onyx', 'shimmer']);
        if (supported.has(voice)) return voice;
        if (voice === 'male') return 'alloy';
        if (voice === 'female') return 'nova';
        if (voice === 'neutral') return 'echo';
        return 'nova';
      };

      const voice = normalizeVoice(request.voice);

      // Prefer ElevenLabs first; fallback to legacy
      const el = await elevenLabsService.tts({ text: request.text });
      if (el.success && el.audioUrl) {
        return { success: true, audioUrl: el.audioUrl };
      }
      // Note: deprecated in favor of ElevenLabs. Keep as backup only.
      const encodedText = encodeURIComponent(request.text);
      const legacyUrl = `https://text.pollinations.ai/${encodedText}?model=openai-audio&voice=${voice}`;
      console.log('[PollinationsAudio] Trying legacy TTS endpoint:', legacyUrl);

      try {
        const head = await fetch(legacyUrl, {
          method: 'HEAD',
          headers: {
            'HTTP-Referer': `https://${this.referrer}`,
            ...(this.apiKey && { 'Authorization': `Bearer ${this.apiKey}` })
          }
        });
        // Many CDNs do not support HEAD properly; accept 200-404 and still try to play URL
        if (head.ok || head.status === 404 || head.status === 200) {
          console.log('[PollinationsAudio] Legacy endpoint reachable, returning URL');
          return { success: true, audioUrl: legacyUrl };
        }
      } catch (e) {
        console.warn('[PollinationsAudio] Legacy HEAD check failed, proceeding to POST API fallback:', e);
      }

      // Fallback to OpenAI-compatible POST API if legacy URL not viable
      const payload = {
        model: 'openai-tts',
        input: request.text,
        voice,
        response_format: 'mp3',
        speed: 1.0
      };

      console.log('[PollinationsAudio] TTS Payload (POST fallback):', payload);

      const ttsEndpoint = 'https://text.pollinations.ai/openai/v1/audio/speech';
      const response = await fetch(ttsEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'HTTP-Referer': `https://${this.referrer}`,
          ...(this.apiKey && { 'Authorization': `Bearer ${this.apiKey}` })
        },
        body: JSON.stringify(payload)
      });

      console.log('[PollinationsAudio] TTS Response status (POST fallback):', response.status);
      console.log('[PollinationsAudio] TTS Response headers (POST fallback):', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[PollinationsAudio] TTS API error (POST fallback):', errorText);
        throw new Error(`TTS API error (${response.status}): ${errorText}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType?.includes('audio/')) {
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        console.log('[PollinationsAudio] ===== TTS SUCCESS (Audio Blob via POST) =====');
        return { success: true, audioUrl, data: { blob: audioBlob } };
      } else {
        const data = await response.json();
        const audioUrl = data.audioUrl || data.url || data.audio_url || '';
        console.log('[PollinationsAudio] ===== TTS SUCCESS (JSON via POST) =====');
        return { success: true, data, audioUrl };
      }

    } catch (error) {
      console.error('[PollinationsAudio] ===== TTS FAILED =====');
      console.error('[PollinationsAudio] TTS Error:', error);
      // As a final attempt, return the legacy URL even if checks failed
      try {
        const fallbackVoice = (request.voice || 'nova');
        const encodedText = encodeURIComponent(request.text);
        const fallbackUrl = `https://text.pollinations.ai/${encodedText}?model=openai-audio&voice=${fallbackVoice}`;
        console.warn('[PollinationsAudio] Returning legacy URL as best-effort fallback:', fallbackUrl);
        return { success: true, audioUrl: fallbackUrl };
      } catch {}

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown TTS error'
      };
    }
  }

  /**
   * Record audio from microphone and convert to base64
   */
  async recordAudio(durationMs: number = 5000): Promise<string | null> {
    try {
      console.log('[PollinationsAudio] Starting audio recording...');
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const audioChunks: Blob[] = [];

      return new Promise((resolve, reject) => {
        mediaRecorder.ondataavailable = (event) => {
          audioChunks.push(event.data);
        };

        mediaRecorder.onstop = async () => {
          const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
          const arrayBuffer = await audioBlob.arrayBuffer();
          const base64Audio = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
          
          console.log('[PollinationsAudio] Recording completed, base64 length:', base64Audio.length);
          resolve(base64Audio);
          
          // Stop all tracks
          stream.getTracks().forEach(track => track.stop());
        };

        mediaRecorder.onerror = (error) => {
          console.error('[PollinationsAudio] Recording error:', error);
          reject(error);
        };

        mediaRecorder.start();
        
        // Stop recording after specified duration
        setTimeout(() => {
          if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
          }
        }, durationMs);
      });

    } catch (error) {
      console.error('[PollinationsAudio] Microphone access error:', error);
      return null;
    }
  }

  /**
   * Play audio from URL or blob
   */
  async playAudio(audioUrl: string): Promise<void> {
    try {
      console.log('[PollinationsAudio] Playing audio:', audioUrl);
      
      const audio = new Audio(audioUrl);
      audio.play();
      
      return new Promise((resolve, reject) => {
        audio.onended = () => {
          console.log('[PollinationsAudio] Audio playback completed');
          resolve();
        };
        
        audio.onerror = (error) => {
          console.error('[PollinationsAudio] Audio playback error:', error);
          reject(error);
        };
      });
      
    } catch (error) {
      console.error('[PollinationsAudio] Audio play error:', error);
      throw error;
    }
  }

  /**
   * Get available voices for TTS
   */
  getAvailableVoices(): string[] {
    return ['nova', 'alloy', 'echo', 'fable', 'onyx', 'shimmer'];
  }

  /**
   * Test the audio service
   */
  async testService(): Promise<boolean> {
    try {
      const testResult = await this.textToSpeech({
        text: "Test audio generation",
        voice: "nova"
      });
      
      return testResult.success;
    } catch (error) {
      console.error('[PollinationsAudio] Service test failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const pollinationsAudioService = PollinationsAudioService.getInstance();
export default pollinationsAudioService;